'use client';

import {
  COMPANY_COUNTRIES,
  countryScopeOptions,
  isCompanyCountryCode,
  swissCantonLabel,
} from '@/lib/countries';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Check, ChevronDown, Download, Search, SlidersHorizontal, X } from 'lucide-react';
import type * as XLSXType from 'xlsx';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/api';
import {
  SWISS_CANTON_NAMES,
  tryBuildCompanySlugFromCanonical,
  type CompanyCountryCode,
} from '@/lib/company-utils';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Card, CardFooter, CardTable } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LocaleLink } from '@/components/locale-link';
import {
  DataGrid,
  DataGridApiFetchParams,
} from '@/components/ui/data-grid';
import { DataGridColumnHeader } from '@/components/ui/data-grid-column-header';
import { DataGridColumnVisibility } from '@/components/ui/data-grid-column-visibility';
import { DataGridPagination } from '@/components/ui/data-grid-pagination';
import { DataGridTable } from '@/components/ui/data-grid-table';
import { Input } from '@/components/ui/input';
import { FeedAlertTrigger } from '@/new_components/alerts/FeedAlertTrigger';
import { FilterSidebarPanel } from '@/new_components/searchresults/FilterSidebarPanel';
import { CountryScopeControl } from '@/new_components/shared/CountryScopeControl';
import {
  FilterChipBar,
  type FilterChip,
} from '@/new_components/searchresults/FilterChipBar';
import {
  FilterLabel,
  FilterCheckbox,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  INPUT_PLACEHOLDER_CLASS,
} from '@/new_components/searchresults/FilterControls';
import { FilterValuePicker } from '@/new_components/searchresults/FilterValuePicker';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';

export type TendersResponse = {
  data: TenderRow[];
  pagination: { page: number; limit: number; total: number };
};

type TenderRow = {
  id: number;
  publication_number: string;
  publication_date: string | null;
  title: string | null;
  buyer_name: string | null;
  buyer_town: string | null;
  buyer_country_code: string | null;
  bundesland: string | null;
  canton: string | null;
  contract_type: string | null;
  procedure_type: string | null;
  estimated_value: number | null;
  value_currency: string | null;
  cpv_codes: string[] | null;
  cpv_descriptions: (string | null)[] | null;
  nuts_codes: string[] | null;
  submission_deadline: string | null;
  document_type: string | null;
  winner_name: string | null;
  awarded_value: number | null;
  awarded_currency: string | null;
  highest_tender_value: number | null;
  lowest_tender_value: number | null;
  buyer_street: string | null;
  buyer_postal_code: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  buyer_company_id: string | null;
  buyer_company_size: string | null;
  buyer_eu_id: string | null;
  buyer_canonical_register_court: string | null;
  buyer_canonical_register_type: string | null;
  buyer_canonical_register_id: string | null;
  buyer_canonical_sitz: string | null;
  buyer_canonical_name: string | null;
  winner_town: string | null;
  winner_street: string | null;
  winner_postal_code: string | null;
  winner_url: string | null;
  winner_email: string | null;
  winner_phone: string | null;
  winner_company_id: string | null;
  winner_company_size: string | null;
  winner_eu_id: string | null;
  winner_canonical_register_court: string | null;
  winner_canonical_register_type: string | null;
  winner_canonical_register_id: string | null;
  winner_canonical_sitz: string | null;
  winner_canonical_name: string | null;
  external_reference_url: string | null;
  xml_format: string | null;
  parse_error: string | null;
};

function formatCurrency(value: number | null, currency: string | null): string {
  if (value == null) return '\u2014';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return new Date(dateStr).toLocaleDateString('de-DE');
  } catch {
    return dateStr;
  }
}

const VALUE_RANGES: Record<string, { min?: string; max?: string }> = {
  '0-100k': { max: '100000' },
  '100k-1m': { min: '100000', max: '1000000' },
  '1m-10m': { min: '1000000', max: '10000000' },
  '10m-50m': { min: '10000000', max: '50000000' },
  '50m+': { min: '50000000' },
};

function valueRangeToParams(range: string): Record<string, string> {
  const r = VALUE_RANGES[range];
  if (!r) return {};
  const params: Record<string, string> = {};
  if (r.min) params.value_min = r.min;
  if (r.max) params.value_max = r.max;
  return params;
}

const contractTypeBadge: Record<string, string> = {
  works: 'destructive',
  supplies: 'warning',
  services: 'info',
};

function tenderRegion(
  row: TenderRow,
  country: CompanyCountryCode,
  lang: string,
): string | null {
  if (country === 'CH') {
    return swissCantonLabel(row.canton || row.bundesland, lang) || null;
  }
  return row.bundesland;
}

const TendersList = ({
  isGuest,
  canExport = false,
  onSignIn,
  initialData,
}: {
  isGuest?: boolean;
  /** Whether the user may export (Pro/Admin). The button shows for all
   *  authenticated users; non-entitled users get a "Pro feature" prompt. */
  canExport?: boolean;
  onSignIn?: () => void;
  initialData?: TendersResponse | null;
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const cantonOptions = useMemo(
    () =>
      Object.keys(SWISS_CANTON_NAMES)
        .map((code) => ({ value: code, label: swissCantonLabel(code, lang) }))
        .sort((a, b) => a.label.localeCompare(b.label, lang)),
    [lang],
  );
  const { locale } = useLocale();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: isGuest ? 20 : 50,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'publication_date', desc: true },
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContractType, setSelectedContractType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [country, setCountry] = useState<CompanyCountryCode>('DE');
  const [selectedBundesland, setSelectedBundesland] = useState('all');
  const [selectedCanton, setSelectedCanton] = useState('all');
  const [selectedValueRange, setSelectedValueRange] = useState('all');
  const [selectedCpvCode, setSelectedCpvCode] = useState('');
  const [cpvCodesOpen, setCpvCodesOpen] = useState(false);
  const [cpvSearch, setCpvSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cpvCodesList, setCpvCodesList] = useState<{ code: string; description: string }[]>([]);
  const cpvListRef = useRef<HTMLDivElement>(null);
  const cpvDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Match the insolvency page: hydrate deep-link filters after mount.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const urlCountry = sp.get('country')?.toUpperCase();
    if (isCompanyCountryCode(urlCountry)) setCountry(urlCountry);
    const canton = sp.get('canton')?.toUpperCase();
    if (canton && SWISS_CANTON_NAMES[canton]) setSelectedCanton(canton);
  }, []);

  const regionLabel = t(
    country === 'CH' ? 'pages.insolvenzticker.canton' : 'tenders.bundesland',
  );
  // DE is the backend default; any other country is always sent (or the
  // request gets German tenders). Send only the active country's region
  // filter — Bundesland names for DE / AT, canton codes for CH.
  const regionParams: Record<string, string> =
    country === 'CH'
      ? {
          country: 'CH',
          ...(selectedCanton !== 'all' ? { canton: selectedCanton } : {}),
        }
      : {
          ...(country !== 'DE' ? { country } : {}),
          ...(selectedBundesland !== 'all'
            ? { bundesland: selectedBundesland }
            : {}),
        };

  // Load top-level CPV codes on mount, then narrow on search
  useEffect(() => {
    apiFetch('/api/search/cpv-codes')
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (cpvDebounceRef.current) clearTimeout(cpvDebounceRef.current);
    if (cpvSearch.length >= 2) {
      cpvDebounceRef.current = setTimeout(() => {
        apiFetch(`/api/search/cpv-codes?query=${encodeURIComponent(cpvSearch)}`)
          .then((res) => res.json())
          .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
          .catch(() => {});
      }, 200);
    } else if (cpvSearch.length === 0) {
      apiFetch('/api/search/cpv-codes')
        .then((res) => res.json())
        .then((data) => { if (Array.isArray(data)) setCpvCodesList(data); })
        .catch(() => {});
    }
    return () => { if (cpvDebounceRef.current) clearTimeout(cpvDebounceRef.current); };
  }, [cpvSearch]);

  const fetchTenders = async ({
    pageIndex,
    pageSize,
    sorting,
    searchQuery,
  }: DataGridApiFetchParams) => {
    const sortField = sorting?.[0]?.id || '';
    const sortDirection = sorting?.[0]?.desc ? 'desc' : 'asc';

    const params = new URLSearchParams({
      page: String(pageIndex + 1),
      limit: String(pageSize),
      ...(sortField ? { sort: sortField, dir: sortDirection } : {}),
      ...(searchQuery ? { query: searchQuery } : {}),
      ...(selectedContractType !== 'all'
        ? { contract_type: selectedContractType }
        : {}),
      ...(selectedStatus !== 'all' ? { status: selectedStatus } : {}),
      ...regionParams,
      ...(selectedValueRange !== 'all' ? valueRangeToParams(selectedValueRange) : {}),
      ...(selectedCpvCode ? { cpv_code: selectedCpvCode } : {}),
    });

    const response = await apiFetch(
      `/api/tenders?${params.toString()}`,
    );

    if (!response.ok) {
      throw new Error('Failed to fetch tenders');
    }

    return response.json();
  };

  // Build the active-filter query params (without pagination) shared by the
  // grid fetch and the export.
  const buildFilterParams = (): Record<string, string> => ({
    ...(searchQuery ? { query: searchQuery } : {}),
    ...(selectedContractType !== 'all'
      ? { contract_type: selectedContractType }
      : {}),
    ...(selectedStatus !== 'all' ? { status: selectedStatus } : {}),
    ...regionParams,
    ...(selectedValueRange !== 'all'
      ? valueRangeToParams(selectedValueRange)
      : {}),
    ...(selectedCpvCode ? { cpv_code: selectedCpvCode } : {}),
  });

  // Excel export — re-fetches the current result set (same filters + sort) up
  // to EXPORT_MAX rows and writes an .xlsx, mirroring the /search export.
  // Export is Pro/Admin only; non-entitled users get a "Pro feature" prompt.
  const [isExporting, setIsExporting] = useState(false);
  const [showProDialog, setShowProDialog] = useState(false);
  const EXPORT_MAX = 5000;
  const handleExport = async () => {
    if (!canExport) {
      setShowProDialog(true);
      return;
    }
    if (isExporting) return;
    setIsExporting(true);
    try {
      const sortField = sorting?.[0]?.id || '';
      const sortDirection = sorting?.[0]?.desc ? 'desc' : 'asc';
      const params = new URLSearchParams({
        page: '1',
        limit: String(EXPORT_MAX),
        ...(sortField ? { sort: sortField, dir: sortDirection } : {}),
        ...buildFilterParams(),
      });
      const response = await apiFetch(`/api/tenders?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to export tenders');
      const json: TendersResponse = await response.json();
      const XLSX: typeof XLSXType = await import('xlsx');
      const rows = (json.data || []).map((r, i) => ({
        '#': i + 1,
        [t('tenders.columns.pubNr', 'Pub. Nr.')]: r.publication_number,
        [t('tenders.columns.date', 'Date')]: r.publication_date ?? '',
        [t('tenders.columns.title', 'Title')]: r.title ?? '',
        [t('tenders.columns.buyer', 'Buyer')]: r.buyer_name ?? '',
        [t('tenders.columns.town', 'Town')]: r.buyer_town ?? '',
        [regionLabel]: tenderRegion(r, country, lang) ?? '',
        [t('tenders.columns.type', 'Type')]: r.contract_type ?? '',
        [t('tenders.columns.estValue', 'Est. Value')]: r.estimated_value ?? '',
        [t('tenders.columns.winner', 'Winner')]: r.winner_name ?? '',
        [t('tenders.columns.awarded', 'Awarded')]: r.awarded_value ?? '',
        [t('tenders.columns.cpv', 'CPV')]: (r.cpv_codes ?? []).join(', '),
        [t('tenders.columns.deadline', 'Deadline')]: r.submission_deadline ?? '',
        [t('tenders.columns.portal', 'Portal')]: r.external_reference_url ?? '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tenders');
      // Local-time timestamp, e.g. tenders_2026-06-01_14-30-22.xlsx
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const ts = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
      XLSX.writeFile(wb, `tenders_${ts}.xlsx`);
    } catch {
      // silently ignore export errors
    } finally {
      setIsExporting(false);
    }
  };

  // Only seed React Query with the SSR payload when the live query key matches
  // the state the server prefetched (page 0, default sort, no filters). Once
  // the user paginates, sorts, or filters, the key changes and React Query
  // fetches normally.
  const isInitialQuery =
    pagination.pageIndex === 0 &&
    sorting.length === 1 &&
    sorting[0].id === 'publication_date' &&
    sorting[0].desc === true &&
    !searchQuery &&
    selectedContractType === 'all' &&
    selectedStatus === 'all' &&
    country === 'DE' &&
    selectedBundesland === 'all' &&
    selectedCanton === 'all' &&
    selectedValueRange === 'all' &&
    !selectedCpvCode;

  const { data, isLoading } = useQuery({
    queryKey: [
      'admin-tenders',
      pagination,
      sorting,
      searchQuery,
      selectedContractType,
      selectedStatus,
      country,
      selectedBundesland,
      selectedCanton,
      selectedValueRange,
      selectedCpvCode,
    ],
    queryFn: () =>
      fetchTenders({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting,
        searchQuery,
      }),
    initialData: isInitialQuery && initialData ? initialData : undefined,
    // TED data changes at most daily. With staleTime 0 React Query refetched
    // the SSR'd page 1 straight after hydration and again on every tab focus,
    // so each visit ran the (slow) backend query twice. The default gcTime
    // keeps visited pages cached, so paging back is instant.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    // Keep the previous page on screen while the next one loads, instead of
    // dropping back to the skeleton (isLoading stays false meanwhile).
    placeholderData: keepPreviousData,
    retry: 1,
  });

  const columns = useMemo<ColumnDef<TenderRow>[]>(
    () => [
      {
        accessorKey: 'publication_number',
        id: 'publication_number',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.pubNr', 'Pub. Nr.')} column={column} />
        ),
        cell: ({ row }) => (
          <a
            href={`https://ted.europa.eu/de/notice/-/detail/${row.original.publication_number}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs max-w-[120px] truncate text-blue-600 hover:underline dark:text-blue-400"
            title={row.original.publication_number}
          >
            {row.original.publication_number}
          </a>
        ),
        size: 130,
        meta: {
          headerTitle: t('tenders.columns.pubNr', 'Pub. Nr.'),
          skeleton: <Skeleton className="w-24 h-7" />,
        },
        enableSorting: true,
        enableHiding: false,
      },
      {
        accessorKey: 'publication_date',
        id: 'publication_date',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.date', 'Date')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm whitespace-nowrap">
            {formatDate(row.original.publication_date)}
          </div>
        ),
        size: 100,
        meta: { headerTitle: t('tenders.columns.date', 'Date'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'title',
        id: 'title',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.title', 'Title')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm max-w-[300px] truncate" title={row.original.title ?? ''}>
            {row.original.title || '\u2014'}
          </div>
        ),
        size: 300,
        meta: { headerTitle: t('tenders.columns.title', 'Title'), skeleton: <Skeleton className="w-48 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'buyer_name',
        id: 'buyer_name',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.buyer', 'Buyer')} column={column} />
        ),
        cell: ({ row }) => {
          const name = row.original.buyer_name;
          const euId = row.original.buyer_eu_id;
          if (!name) return <span className="text-muted-foreground">{'\u2014'}</span>;
          const slug = tryBuildCompanySlugFromCanonical(
            {
              canonical_name: row.original.buyer_canonical_name,
              canonical_sitz: row.original.buyer_canonical_sitz,
              canonical_register_court: row.original.buyer_canonical_register_court,
              canonical_register_type: row.original.buyer_canonical_register_type,
              canonical_register_id: row.original.buyer_canonical_register_id,
            },
            locale,
          );
          if (slug) {
            return (
              <a
                href={slug}
                className="text-sm max-w-[200px] truncate text-blue-600 hover:underline dark:text-blue-400 block"
                title={`${name} (${euId ?? ''})`}
              >
                {name}
              </a>
            );
          }
          return (
            <div className="text-sm max-w-[200px] truncate" title={name}>{name}</div>
          );
        },
        size: 200,
        meta: { headerTitle: t('tenders.columns.buyer', 'Buyer'), skeleton: <Skeleton className="w-32 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'buyer_town',
        id: 'buyer_town',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.town', 'Town')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm">{row.original.buyer_town || '\u2014'}</div>
        ),
        size: 120,
        meta: { headerTitle: t('tenders.columns.town', 'Town'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        id: 'region',
        accessorFn: (row) => tenderRegion(row, country, lang),
        header: ({ column }) => (
          <DataGridColumnHeader title={regionLabel} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm">
            {tenderRegion(row.original, country, lang) || '\u2014'}
          </div>
        ),
        size: country === 'CH' ? 130 : 160,
        meta: {
          headerTitle: regionLabel,
          skeleton: <Skeleton className="w-20 h-7" />,
        },
        enableSorting: false,
      },
      {
        accessorKey: 'contract_type',
        id: 'contract_type',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.type', 'Type')} column={column} />
        ),
        cell: ({ row }) => {
          const ct = row.original.contract_type;
          if (!ct) return <span className="text-muted-foreground">{'\u2014'}</span>;
          const variant = contractTypeBadge[ct] as 'destructive' | 'warning' | 'info' | undefined;
          return (
            <Badge variant={variant ?? 'secondary'} appearance="ghost">
              {ct}
            </Badge>
          );
        },
        size: 100,
        meta: { headerTitle: t('tenders.columns.type', 'Type'), skeleton: <Skeleton className="w-16 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'estimated_value',
        id: 'estimated_value',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.estValue', 'Est. Value')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm text-right tabular-nums whitespace-nowrap">
            {formatCurrency(row.original.estimated_value, row.original.value_currency)}
          </div>
        ),
        size: 130,
        meta: { headerTitle: t('tenders.columns.estValue', 'Est. Value'), skeleton: <Skeleton className="w-24 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'winner_name',
        id: 'winner_name',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.winner', 'Winner')} column={column} />
        ),
        cell: ({ row }) => {
          const name = row.original.winner_name;
          const euId = row.original.winner_eu_id;
          if (!name) return <span className="text-muted-foreground">{'\u2014'}</span>;
          const slug = tryBuildCompanySlugFromCanonical(
            {
              canonical_name: row.original.winner_canonical_name,
              canonical_sitz: row.original.winner_canonical_sitz,
              canonical_register_court: row.original.winner_canonical_register_court,
              canonical_register_type: row.original.winner_canonical_register_type,
              canonical_register_id: row.original.winner_canonical_register_id,
            },
            locale,
          );
          if (slug) {
            return (
              <a
                href={slug}
                className="text-sm max-w-[200px] truncate text-blue-600 hover:underline dark:text-blue-400 block"
                title={`${name} (${euId ?? ''})`}
              >
                {name}
              </a>
            );
          }
          return (
            <div className="text-sm max-w-[200px] truncate" title={name}>{name}</div>
          );
        },
        size: 200,
        meta: { headerTitle: t('tenders.columns.winner', 'Winner'), skeleton: <Skeleton className="w-32 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'awarded_value',
        id: 'awarded_value',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.awarded', 'Awarded')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm text-right tabular-nums whitespace-nowrap">
            {formatCurrency(row.original.awarded_value, row.original.awarded_currency)}
          </div>
        ),
        size: 130,
        meta: { headerTitle: t('tenders.columns.awarded', 'Awarded'), skeleton: <Skeleton className="w-24 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'lowest_tender_value',
        id: 'lowest_tender_value',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.lowestBid', 'Lowest Bid')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm text-right tabular-nums whitespace-nowrap">
            {formatCurrency(row.original.lowest_tender_value, row.original.awarded_currency)}
          </div>
        ),
        size: 120,
        meta: { headerTitle: t('tenders.columns.lowestBid', 'Lowest Bid'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'highest_tender_value',
        id: 'highest_tender_value',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.highestBid', 'Highest Bid')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm text-right tabular-nums whitespace-nowrap">
            {formatCurrency(row.original.highest_tender_value, row.original.awarded_currency)}
          </div>
        ),
        size: 120,
        meta: { headerTitle: t('tenders.columns.highestBid', 'Highest Bid'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'cpv_codes',
        id: 'cpv_codes',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.cpv', 'CPV')} column={column} />
        ),
        cell: ({ row }) => {
          const codes = row.original.cpv_codes;
          const descs = row.original.cpv_descriptions;
          if (!codes?.length) return <span className="text-muted-foreground">{'\u2014'}</span>;
          const label = descs?.[0] ?? codes[0];
          const title = codes.map((c, i) => `${c} — ${descs?.[i] ?? c}`).join('\n');
          return (
            <div className="flex items-center gap-1.5 whitespace-nowrap" title={title}>
              <span className="text-[10px] font-mono text-muted-foreground">{codes[0]}</span>
              <span className="text-[10px] text-muted-foreground truncate">{label}</span>
              {codes.length > 1 && (
                <span className="text-[10px] text-muted-foreground shrink-0">+{codes.length - 1}</span>
              )}
            </div>
          );
        },
        size: 220,
        meta: { headerTitle: t('tenders.columns.cpv', 'CPV'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'document_type',
        id: 'document_type',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.docType', 'Doc Type')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-xs max-w-[140px] truncate" title={row.original.document_type ?? ''}>
            {row.original.document_type || '\u2014'}
          </div>
        ),
        size: 120,
        meta: { headerTitle: t('tenders.columns.docType', 'Doc Type'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'submission_deadline',
        id: 'submission_deadline',
        header: ({ column }) => (
          <DataGridColumnHeader title={t('tenders.columns.deadline', 'Deadline')} column={column} />
        ),
        cell: ({ row }) => (
          <div className="text-sm whitespace-nowrap">
            {formatDate(row.original.submission_deadline)}
          </div>
        ),
        size: 100,
        meta: { headerTitle: t('tenders.columns.deadline', 'Deadline'), skeleton: <Skeleton className="w-20 h-7" /> },
        enableSorting: true,
      },
      {
        accessorKey: 'external_reference_url',
        id: 'external_reference_url',
        header: () => <div className="text-xs">{t('tenders.columns.portal', 'Portal')}</div>,
        cell: ({ row }) => {
          const url = row.original.external_reference_url;
          if (!url) return <span className="text-muted-foreground">{'\u2014'}</span>;
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline dark:text-blue-400 text-xs truncate max-w-[120px] block"
              title={url}
            >
              {t('tenders.columns.link', 'Link')}
            </a>
          );
        },
        size: 70,
        meta: { headerTitle: t('tenders.columns.portal', 'Portal'), skeleton: <Skeleton className="w-10 h-7" /> },
        enableSorting: false,
      },
    ],
    [t, locale, lang, country, regionLabel],
  );

  const table = useReactTable({
    data: data?.data || [],
    columns,
    pageCount: Math.ceil((data?.pagination?.total || 0) / pagination.pageSize),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  const resetPage = () =>
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  const handleCountryChange = (next: CompanyCountryCode) => {
    if (next === country) return;
    setCountry(next);
    setSelectedBundesland('all');
    setSelectedCanton('all');
    resetPage();
    const sp = new URLSearchParams(window.location.search);
    if (next !== 'DE') sp.set('country', next);
    else sp.delete('country');
    sp.delete('bundesland');
    sp.delete('canton');
    const qs = sp.toString();
    window.history.replaceState(
      null, '', qs ? `?${qs}` : window.location.pathname,
    );
  };
  const regionOptions =
    country === 'CH'
      ? cantonOptions
      : COMPANY_COUNTRIES[country].regions.map((value) => ({ value, label: value }));
  const selectedRegion = country === 'CH' ? selectedCanton : selectedBundesland;
  const setSelectedRegion =
    country === 'CH' ? setSelectedCanton : setSelectedBundesland;
  const contractOptions = ['works', 'supplies', 'services'].map((value) => ({
    value,
    label: t(`tenders.${value}`),
  }));
  const statusOptions = ['open', 'awarded', 'closed'].map((value) => ({
    value,
    label: t(`tenders.${value}`),
  }));
  const valueOptions = [
    { value: '0-100k', label: t('tenders.valueUnder100k') },
    { value: '100k-1m', label: t('tenders.value100kTo1m') },
    { value: '1m-10m', label: t('tenders.value1mTo10m') },
    { value: '10m-50m', label: t('tenders.value10mTo50m') },
    { value: '50m+', label: t('tenders.valueOver50m') },
  ];
  const chips: FilterChip[] = [];
  if (searchQuery)
    chips.push({
      key: 'search',
      label: searchQuery,
      onRemove: () => {
        setSearchQuery('');
        resetPage();
      },
    });
  for (const [key, value, options, setter] of [
    [
      'contract',
      selectedContractType,
      contractOptions,
      setSelectedContractType,
    ],
    ['status', selectedStatus, statusOptions, setSelectedStatus],
    ['value', selectedValueRange, valueOptions, setSelectedValueRange],
  ] as const) {
    if (value !== 'all')
      chips.push({
        key,
        label: options.find((option) => option.value === value)?.label ?? value,
        onRemove: () => {
          setter('all');
          resetPage();
        },
      });
  }
  if (selectedRegion !== 'all')
    chips.push({
      key: country === 'CH' ? 'canton' : 'bundesland',
      label:
        regionOptions.find((option) => option.value === selectedRegion)?.label ?? selectedRegion,
      onRemove: () => {
        setSelectedRegion('all');
        resetPage();
      },
    });
  if (selectedCpvCode)
    chips.push({
      key: 'cpv',
      label: `${t('tenders.cpvCode')}: ${selectedCpvCode}`,
      onRemove: () => {
        setSelectedCpvCode('');
        resetPage();
      },
    });
  const activeFilterCount = chips.length;
  useEffect(() => {
    const onOpen = () => setFiltersOpen(true);
    window.addEventListener('firmendata:open-filters', onOpen);
    return () => window.removeEventListener('firmendata:open-filters', onOpen);
  }, []);
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('firmendata:filter-count', { detail: activeFilterCount }),
    );
  }, [activeFilterCount]);
  // The page's filters in the tenders feed shape (backend
  // `backend/alerts/feeds`, the same filter vocabulary as GET /tenders).
  const alertFilters: Record<string, unknown> = { country };
  if (country === 'CH' && selectedCanton !== 'all') alertFilters.canton = selectedCanton;
  if (country !== 'CH' && selectedBundesland !== 'all') {
    alertFilters.bundesland = selectedBundesland;
  }
  if (selectedCpvCode) alertFilters.cpv_prefixes = [selectedCpvCode];
  if (selectedContractType !== 'all') alertFilters.contract_type = selectedContractType;
  if (selectedStatus !== 'all') alertFilters.status = selectedStatus;
  if (selectedValueRange !== 'all') {
    const range = VALUE_RANGES[selectedValueRange];
    if (range?.min) alertFilters.value_min = Number(range.min);
    if (range?.max) alertFilters.value_max = Number(range.max);
  }
  if (searchQuery.trim()) alertFilters.keyword = searchQuery.trim();
  const alertUnfiltered = Object.keys(alertFilters).length === 1;

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedContractType('all');
    setSelectedStatus('all');
    setSelectedValueRange('all');
    setSelectedBundesland('all');
    setSelectedCanton('all');
    setSelectedCpvCode('');
    resetPage();
  };

  return (
    <div className="search-warm-bg relative lg:fixed lg:left-[var(--app-sidebar-width,0px)] lg:right-0 lg:w-[calc(100%-var(--app-sidebar-width,0px))] lg:top-[var(--header-height,95px)] lg:bottom-[calc(var(--footer-height,60px)+env(safe-area-inset-bottom,0px))] flex flex-col lg:flex-row min-h-0 w-full max-w-full lg:overflow-hidden bg-background">
      <FilterSidebarPanel
        id="tender-filters"
        ariaLabel={t('search.filters')}
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        footer={
          <Button className="w-full h-11" onClick={() => setFiltersOpen(false)}>
            {t('search.showFilterResults', {
              count: (data?.pagination?.total ?? 0).toLocaleString(locale),
            })}
          </Button>
        }
      >
        <div className="space-y-4">
          <CountryScopeControl
            value={country}
            options={countryScopeOptions('tenderScope')}
            onChange={(next) => {
              if (isCompanyCountryCode(next)) handleCountryChange(next);
            }}
          />
          <div className="space-y-1.5">
            <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
              {t('tenders.search')}
            </FilterLabel>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <Input
                placeholder={t('tenders.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  resetPage();
                }}
                className={`h-11 lg:h-9 text-base lg:text-sm pl-8 pr-11 ${INPUT_PLACEHOLDER_CLASS}`}
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label={t('search.activeFilters.clearSearch')}
                  onClick={() => {
                    setSearchQuery('');
                    resetPage();
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 size-11 lg:size-9 flex items-center justify-center"
                >
                  <X className="size-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
          {[
            {
              key: 'contractType',
              options: contractOptions,
              value: selectedContractType,
              setter: setSelectedContractType,
            },
            {
              key: 'status',
              options: statusOptions,
              value: selectedStatus,
              setter: setSelectedStatus,
            },
            {
              key: 'valueRange',
              options: valueOptions,
              value: selectedValueRange,
              setter: setSelectedValueRange,
            },
          ].map((field) => (
            <div key={field.key} className="space-y-1.5">
              <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
                {t(`tenders.${field.key}`)}
              </FilterLabel>
              <div role="radiogroup" aria-label={t(`tenders.${field.key}`)}>
                {field.options.map((option) => (
                  <FilterCheckbox
                    key={option.value}
                    role="radio"
                    lock={null}
                    label={option.label}
                    checked={field.value === option.value}
                    onCheckedChange={(checked) => {
                      field.setter(checked ? option.value : 'all');
                      resetPage();
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
          <div className="space-y-1.5">
            <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
              {regionLabel}
            </FilterLabel>
            <FilterValuePicker
              label={
                country === 'CH' ? regionLabel : t('tenders.allBundeslaender')
              }
              options={regionOptions}
              selected={
                selectedRegion === 'all' ? [] : [selectedRegion]
              }
              single
              onToggle={(value) => {
                setSelectedRegion(
                  selectedRegion === value ? 'all' : value,
                );
                resetPage();
              }}
              onRemove={() => {
                setSelectedRegion('all');
                resetPage();
              }}
            />
          </div>
          <div className="space-y-1.5">
            <FilterLabel lock={null} className={FIELD_LABEL_CLASS}>
              {t('tenders.cpvCode', 'CPV Code')}
            </FilterLabel>
            <Popover
              open={cpvCodesOpen}
              onOpenChange={(open) => {
                setCpvCodesOpen(open);
                if (!open) setCpvSearch('');
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full min-h-11 lg:min-h-9 rounded-lg h-auto font-normal text-left justify-start py-1.5 px-3 relative"
                >
                  <div className="flex flex-wrap gap-1 w-full min-w-0 pr-5">
                    {!selectedCpvCode ? (
                      <span className={FIELD_PLACEHOLDER_CLASS}>
                        {t(
                          'tenders.cpvPlaceholder',
                          'e.g. 45000000 Construction work',
                        )}
                      </span>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="max-w-full text-xs font-normal gap-1 pr-1 border border-foreground"
                      >
                        <span className="font-mono text-muted-foreground">
                          {selectedCpvCode}
                        </span>
                        <span className="max-w-[120px] truncate">
                          {cpvCodesList.find((c) => c.code === selectedCpvCode)
                            ?.description || ''}
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label={t('search.activeFilters.remove', {
                            label: selectedCpvCode,
                          })}
                          className="inline-flex min-h-11 min-w-11 lg:min-h-4 lg:min-w-4 shrink-0 items-center justify-center cursor-pointer hover:bg-muted rounded-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCpvCode('');
                            resetPage();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedCpvCode('');
                              resetPage();
                            }
                          }}
                        >
                          <X size={12} />
                        </span>
                      </Badge>
                    )}
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 shrink-0 opacity-50" />
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    className={INPUT_PLACEHOLDER_CLASS}
                    placeholder={t(
                      'tenders.cpvSearch',
                      'Search CPV code (min. 2 digits)...',
                    )}
                    value={cpvSearch}
                    onValueChange={(v) => {
                      setCpvSearch(v);
                      if (cpvListRef.current) cpvListRef.current.scrollTop = 0;
                    }}
                  />
                  <CommandList
                    ref={cpvListRef}
                    className="h-[272px] overflow-y-auto"
                  >
                    <CommandEmpty>
                      {t('tenders.cpvEmpty', 'No CPV code found.')}
                    </CommandEmpty>
                    <CommandGroup>
                      {cpvCodesList.map((cpv) => (
                        <CommandItem
                          key={cpv.code}
                          value={`${cpv.code} ${cpv.description}`}
                          className="min-h-11 lg:min-h-9"
                          onSelect={() => {
                            setSelectedCpvCode(cpv.code);
                            setCpvCodesOpen(false);
                            setCpvSearch('');
                            setPagination({ ...pagination, pageIndex: 0 });
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedCpvCode === cpv.code
                                ? 'opacity-100'
                                : 'opacity-0',
                            )}
                          />
                          <span className="font-mono text-xs text-muted-foreground mr-1.5">
                            {cpv.code}
                          </span>
                          <span className="truncate text-xs">
                            {cpv.description}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </FilterSidebarPanel>
      <main className="lg:flex-1 min-w-0 flex flex-col relative lg:overflow-hidden">
        <div className="px-4 pt-4 pb-3 border-b shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-lg lg:text-xl font-semibold">
              {t('tenders.pageTitle')}
            </h1>
            <span className="text-xs text-muted-foreground">
              {data?.pagination?.total != null
                ? t('tenders.results', {
                    count: data.pagination.total.toLocaleString(locale),
                  })
                : '\u00A0'}
            </span>
          </div>
          <p className="mt-1 text-xs lg:text-sm text-muted-foreground">
            {t('tenders.intro')}
          </p>
          <div className="mt-3 flex items-center">
            <FeedAlertTrigger
              feedType="tenders"
              filters={alertFilters}
              isUnfiltered={alertUnfiltered}
              unfilteredDescription={t('alerts.trigger.unfilteredTenders')}
              onAdjustFilters={() => setFiltersOpen(true)}
              signInLabel={t('alerts.trigger.signInTenders')}
              onSignIn={onSignIn}
            />
          </div>
        </div>
        <FilterChipBar
          chips={chips}
          onClearAll={clearAllFilters}
          className="[&_button]:min-h-11 [&_button]:min-w-11 lg:[&_button]:min-h-0 lg:[&_button]:min-w-0"
        />
        {/* Results toolbar — Spalten (column visibility) + Excel export, same
            as the /search results toolbar. Authenticated users only; guests see
            the sign-in overlay instead. */}
        {!isGuest && (
          <div className="flex items-center justify-end gap-1.5 px-3 py-2 border-b bg-background shrink-0">
            <DataGridColumnVisibility
              table={table}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="h-auto min-h-11 lg:min-h-7 py-1 text-[11px] gap-1 rounded-full"
                >
                  <SlidersHorizontal className="h-3 w-3 shrink-0" />
                  {t('search.columns', 'Spalten')}
                </Button>
              }
            />
            <Button
              variant="outline"
              size="sm"
              className="h-auto min-h-11 lg:min-h-7 py-1 text-[11px] gap-1 rounded-full"
              onClick={handleExport}
              disabled={isExporting || !data?.data?.length}
            >
              <Download className="h-3 w-3 shrink-0" />
              {isExporting
                ? t('search.exporting', 'Exportiere…')
                : t('search.exportExcel', 'Excel Export')}
            </Button>
          </div>
        )}

        <div
          className={cn(
            'relative flex-1 min-h-0 flex flex-col',
            isGuest &&
              '[&_tbody_tr:nth-child(n+6)]:blur-sm [&_tbody_tr:nth-child(n+6)]:select-none [&_tbody_tr:nth-child(n+6)]:pointer-events-none',
          )}
        >
          <DataGrid
            table={table}
            recordCount={data?.pagination?.total || 0}
            isLoading={isLoading}
            tableLayout={{
              columnsResizable: true,
              columnsPinnable: true,
              columnsMovable: true,
              columnsVisibility: true,
            }}
            tableClassNames={{ edgeCell: 'px-3' }}
          >
            <Card className="border-0 rounded-none shadow-none flex flex-col flex-1 min-h-0">
              <CardTable className="flex-1 min-h-0">
                <ScrollArea className="h-full w-full">
                  <DataGridTable />
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </CardTable>
              {!isGuest && (
                <CardFooter className="border-t shrink-0">
                  <DataGridPagination />
                </CardFooter>
              )}
            </Card>
          </DataGrid>
          {isGuest && (
            <div className="absolute inset-x-0 bottom-0 top-[200px] flex items-center justify-center z-10">
              <div
                className="flex flex-col items-center justify-center p-8 text-center"
                data-nosnippet
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="space-y-1 text-center">
                    <h3 className="text-lg font-semibold">
                      {t('tenders.euTenders', 'EU Tenders')}
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-[240px] mx-auto">
                      {t(
                        'tenders.signInToView',
                        'Please sign in to view this content.',
                      )}
                    </p>
                  </div>
                  <Button className="mt-2" onClick={() => onSignIn?.()}>
                    {t('tenders.signIn', 'Sign in to view')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Pro-feature prompt shown when a non-entitled user clicks Export. */}
      <Dialog open={showProDialog} onOpenChange={setShowProDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('search.proFeature.title', 'Pro-Funktion')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'search.proFeature.description',
                'Der Excel-Export ist nur für Pro-Nutzer verfügbar. Mit einem Pro-Konto können Sie Suchergebnisse exportieren.',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProDialog(false)}>
              {t('common.buttons.close', 'Schließen')}
            </Button>
            <Button asChild>
              <LocaleLink href="/plans" onClick={() => setShowProDialog(false)}>
                {t('search.proFeature.cta', 'Pläne ansehen')}
              </LocaleLink>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TendersList;
